
import { useCallback } from 'react';
import tournamentService from '../../services/tournamentService';

/**
 * useTimerSync
 * Handles synchronization of the tournament state to the database (Supabase).
 * 
 * @param {string} tournamentId - The ID of the current tournament.
 * @returns {object} Actions to sync state.
 */
export const useTimerSync = (tournamentId) => {

  const syncTimerToDB = useCallback(async (timerState) => {
    if (!tournamentId) return { success: false, error: 'No tournament ID' };

    // Convert internal state to DB payload
    const payload = {
      state: timerState.state,
      rowIndex: timerState.rowIndex,
      endsAt: timerState.endsAt || null,
      pausedAt: timerState.pausedAt || null,
      remaining: timerState.remaining, // Legacy support
      updatedAt: new Date().toISOString(),
    };

    const { data, error } = await tournamentService.updateTournamentState(tournamentId, {
      timer_state: payload
    });

    if (error) return { success: false, error };
    return { success: true, ...data };
  }, [tournamentId]);

  const syncStructureToDB = useCallback(async (name, details, structure, timerState) => {
    if (!tournamentId) return { success: false, error: 'No tournament ID' };

    const { data, error } = await tournamentService.updateTournamentState(tournamentId, {
      name,
      details,
      structure,
      timer_state: timerState
    });

    if (error) return { success: false, error };
    return { success: true, data };
  }, [tournamentId]);

  const updateDetails = useCallback(async (details) => {
    if (!tournamentId) return { success: false, error: 'No tournament ID' };
    const { error } = await tournamentService.updateTournamentState(tournamentId, { details });
    if (error) return { success: false, error };
    return { success: true };
  }, [tournamentId]);

  const updateName = useCallback(async (name) => {
    if (!tournamentId) return { success: false, error: 'No tournament ID' };
    const { error } = await tournamentService.updateTournamentState(tournamentId, { name });
    if (error) return { success: false, error };
    return { success: true };
  }, [tournamentId]);

  return {
    syncTimerToDB,
    syncStructureToDB,
    updateDetails,
    updateName
  };
};
